import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { toast } from 'sonner';
import { Copy, Send, TicketCheck } from 'lucide-react';
import { A } from '@/constants/journey';
import { useLive } from '@/lib/live';
import { buyTicket, copyContract } from '@/components/journey/S00Signal';

const XIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const supplied = (value, fallback) => (!value || String(value).startsWith('{{') ? fallback : value);

export const S07Horizon = () => {
  const { config, setTicketOpen, reducedMotion } = useLive();
  const sectionRef = useRef(null);
  const bloomRef = useRef(null);
  const p = config?.placeholders || {};

  useLayoutEffect(() => {
    if (reducedMotion) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(bloomRef.current, { opacity: 0 }, { opacity: 0.62, scrollTrigger: { trigger: sectionRef.current, start: 'top 60%', end: 'top 10%', scrub: 0.9 } });
    }, sectionRef);
    return () => ctx.revert();
  }, [reducedMotion]);

  const socialClick = (which) => {
    const url = which === 'x' ? p.X_URL : p.TELEGRAM_URL;
    if (!url || url.startsWith('{{')) {
      toast(`${which === 'x' ? 'X' : 'TELEGRAM'} LINK NOT SET.`);
      return;
    }
    window.open(url, '_blank', 'noopener');
  };

  const boardRows = [
    ['SERVICE', supplied(p.TOKEN_NAME, 'THOMAS COIN')],
    ['TICKER', supplied(p.TOKEN_TICKER, 'THOMAS')],
    ['NETWORK', supplied(p.NETWORK, 'SOLANA')],
    ['PLATFORM', supplied(p.LAUNCH_PLATFORM, 'PUMP.FUN')],
    ['LAUNCH DATE', supplied(p.LAUNCH_DATE, '13 AUGUST 2026')],
  ];
  const contract = supplied(p.CONTRACT_ADDRESS, 'OFFICIAL CA UNAVAILABLE');

  return (
    <section ref={sectionRef} className="relative bg-[#080b10]" data-testid="chapter-s07" data-audio-scene="finale" aria-label="The train is here">
      <div className="relative min-h-screen overflow-hidden grain">
        <picture className="absolute inset-0">
          <source media="(max-width: 640px)" srcSet={A.plates.s07m} />
          <img src={A.plates.s07} alt="Coastal sunrise on the Solana railway" className="h-full w-full object-cover" loading="lazy" />
        </picture>
        <div ref={bloomRef} className="pointer-events-none absolute right-[8%] top-[6%] hidden h-[38%] w-[26%] sm:block" style={{ mixBlendMode: 'screen', opacity: reducedMotion ? 0.5 : 0 }}>
          <img src={A.fx.bloom} alt="" className="h-full w-full object-contain" />
        </div>
        <div className="scrim-bottom absolute inset-0" />

        <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl items-end px-4 pb-12 pt-40 sm:px-8 sm:pb-16">
          <div className="grid w-full items-end gap-10 md:grid-cols-[1.1fr_0.9fr]">
            <div className="pt-8 sm:pt-0">
              <h2 className="font-display text-5xl leading-[0.95] tracking-wide text-[#f3e7cf] sm:text-7xl">THE TRAIN'S HERE. ARE YOU?</h2>

              <div className="mt-6 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
                <button type="button" onClick={() => buyTicket(config)} className="btn-mech flex h-12 items-center gap-2 rounded-lg bg-[#1f4e8c] px-5 font-mono text-xs tracking-[0.16em] text-[#f3e7cf] ring-1 ring-white/15 hover:bg-[#1b447b]" data-testid="final-buy-ticket-button"><TicketCheck size={15} /> BUY $THOMAS</button>
                <button type="button" onClick={() => setTicketOpen(true)} className="brass btn-mech flex h-12 items-center gap-2 rounded-lg px-5 font-mono text-xs tracking-[0.16em] text-[#f3e7cf]" data-testid="final-board-train-button">BOARD THE TRAIN</button>
                <button type="button" onClick={() => copyContract(config)} className="btn-mech flex h-12 items-center justify-center gap-2 rounded-lg border border-white/20 px-5 font-mono text-xs tracking-[0.16em] text-[#f3e7cf] sm:justify-start" data-testid="final-copy-contract-button"><Copy size={15} /> COPY CA</button>
              </div>

              <div className="mt-4 flex gap-5">
                <button type="button" onClick={() => socialClick('x')} className="btn-mech flex h-10 items-center gap-2 font-mono text-[10px] tracking-[0.14em] text-[#f3e7cf]/72" data-testid="social-x-button"><XIcon size={13} /> X</button>
                <button type="button" onClick={() => socialClick('tg')} className="btn-mech flex h-10 items-center gap-2 font-mono text-[10px] tracking-[0.14em] text-[#f3e7cf]/72" data-testid="social-telegram-button"><Send size={13} /> TELEGRAM</button>
              </div>
            </div>

            <div className="departure-board-clean p-5 sm:p-6" data-testid="final-departure-board">
              <div className="mb-3 border-b border-white/10 pb-2.5 font-mono text-[10px] tracking-[0.3em] text-[#c9a24a]">DEPARTURE BOARD</div>
              <div className="space-y-2.5">
                {boardRows.map(([label, value]) => (
                  <div key={label} className="flex items-baseline justify-between gap-3">
                    <span className="font-mono text-[9px] tracking-[0.22em] text-[#f3e7cf]/42">{label}</span>
                    <span className="truncate font-mono text-sm text-[#f3e7cf]">{value}</span>
                  </div>
                ))}
                <div className="border-t border-white/10 pt-2.5">
                  <div className="font-mono text-[9px] tracking-[0.22em] text-[#f3e7cf]/42">CONTRACT ADDRESS</div>
                  <div className="select-all break-all font-mono text-xs text-[#f3e7cf]">{contract}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-white/10 bg-[#07090c] px-4 py-6 sm:px-8">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 font-mono text-[9px] leading-relaxed tracking-[0.08em] text-[#f3e7cf]/38 sm:flex-row sm:items-start sm:justify-between">
          <p>THOMAS · SOLANA · 13 AUGUST 2026</p>
          <div className="max-w-2xl space-y-1 sm:text-right">
            <p>ENTERTAINMENT AND COMMUNITY CULTURE. NOT FINANCIAL ADVICE. NO PRICE OR GROWTH PROMISES.</p>
            <p>COMMUNITY-INSPIRED DESTINATIONS ARE NOT PARTNERSHIPS. CONFIRM CHARACTER RIGHTS BEFORE COMMERCIAL LAUNCH.</p>
          </div>
        </div>
      </footer>
    </section>
  );
};
